// File: NavBar2.tsx
import * as React from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Switch,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Icon,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";
import TourGuide from "../../../../features/components/TourGuide";
import NotificationCenter from "./NotificationCenter";

// Icons (prefer default imports for tree-shaking)
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ImageIcon from "@mui/icons-material/Image";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BarChartIcon from "@mui/icons-material/BarChart";
import InfoIcon from "@mui/icons-material/Info";
import LaunchIcon from "@mui/icons-material/Launch";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

interface Props {
  isAuthenticated: boolean;
  logout: () => void;
  email?: string;
  toggleTheme: () => void;
}

type Item = {
  label: string;
  path: string;
  Icon: typeof PeopleIcon;
  external?: boolean;
};

const MENU_ITEMS: Item[] = [
  { label: "Patients", path: "/patients", Icon: PeopleIcon },
  // { label: "Cases", path: "/cases", Icon: AssignmentIcon },
  // { label: "Images", path: "/images", Icon: ImageIcon },
  // { label: "HowTo", path: "/onboarding", Icon: SchoolIcon },
  // { label: "Finalized", path: "/finalizedreports", Icon: CheckCircleIcon },
  // { label: "Analytics", path: "/analytics", Icon: BarChartIcon },
  { label: "SkinAI", path: "https://skin.noetiv.com", Icon: LaunchIcon, external: true },
  { label: "About", path: "/account", Icon: InfoIcon },
  { label: "FirstSteps", path: "/onboarding", Icon: SchoolIcon }

];

export default function NavBar2({
  isAuthenticated,
  email,
  logout,
  toggleTheme,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const [checked, setChecked] = React.useState(
    theme.palette.mode === "dark"
  );

  // Add state for tour guide
  const [runTour, setRunTour] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    toggleTheme();
  };

  const groupBg =
    theme.palette.mode === "dark" ? "#191919" : "#f5f5f7";
  const bubbleBg =
    theme.palette.mode === "dark" ? "#2a2a2e" : "#eeeeef";
  const bubbleHover =
    theme.palette.mode === "dark" ? "#343438" : "#e2e2e6";

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // Define the tour steps
  const tourSteps = [
    {
      target: '[data-tour="patients"]',
      content: 'Access and manage your patient records here',
      disableBeacon: true,
    },
    // {
    //   target: '[data-tour="cases"]',
    //   content: 'View and create patient cases',
    //   disableBeacon: true,
    // },
    {
      target: '[data-tour="about"]',
      content: 'Manage your account settings and subscription',
      disableBeacon: true,
    },
    {
      target: '[data-tour="notification"]',
      content: 'Receive case updates and alerts to follow along on other devices',
      disableBeacon: true,
    },
    {
      target: '[data-tour="theme-switch"]',
      content: 'Toggle between light and dark mode',
      disableBeacon: true,
    },
  ];

  const handleStartTour = (e: React.MouseEvent) => {
    e.preventDefault();
    // Reset the tour completion status in localStorage
    localStorage.removeItem("tour_navbar-tour_completed");
    localStorage.removeItem("tour_navbar-tour_step");
    setRunTour(true);
  };

  const handleFinishTour = () => {
    setRunTour(false);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor:
          theme.palette.mode === "dark" ? "#0f0f10" : "#ffffff",
        color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ 
        justifyContent: "space-between", 
        gap: isMobile ? 0.5 : 2, 
        minHeight: isMobile ? 56 : 72,
        px: isMobile ? 1 : 2
      }}>
        {/* Left: Logo */}
        <Link to={"/"}>
          <img
            src={
              theme.palette.mode === "dark"
                ? "/assets/noetivwhite.png"
                : "/assets/noetivblack.png"
            }
            height={isMobile ? 32 : 44}
            alt="Noetiv"
            loading="lazy"
          />
        </Link>

        {/* Center: Bubble menu (desktop = text + icon; mobile = icons only) */}
        <Box sx={{ 
          flexGrow: 1, 
          display: "flex", 
          justifyContent: "center",
          overflow: "auto",
          mx: isMobile ? 0.5 : 0
        }}>
          <Paper
            elevation={0}
            sx={{
              px: isMobile ? 0.5 : 1,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 0.25 : 0.5,
              borderRadius: 999,
              backgroundColor: groupBg,
              border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
            }}
          >
            {MENU_ITEMS.map(({ label, path, Icon, external }) => {
              const active = !external && isActive(path);
              const isHowTo = label === "FirstSteps";
              
              // Define custom colors for HowTo button
              const howToBgColor = isHowTo ? "#e91e63" : // Pink color for HowTo
                (active ? alpha(theme.palette.primary.main, 0.2) : bubbleBg);
              const howToColor = isHowTo ? "#ffffff" : // White text for better contrast on pink
                (active ? theme.palette.primary.main : theme.palette.text.primary);
              const howToHoverBg = isHowTo ? "#d81b60" : // Darker pink on hover
                (active ? alpha(theme.palette.primary.main, 0.28) : bubbleHover);

              if (isMobile) {
                // ICON-ONLY bubbles on small screens
                return (
                  <Tooltip key={path} title={label} arrow>
                    <IconButton
                      component={isHowTo ? "button" : (external ? "a" : Link)}
                      to={isHowTo || external ? undefined : path}
                      href={external ? path : undefined}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noopener noreferrer" : undefined}
                      aria-label={label}
                      onClick={isHowTo ? handleStartTour : undefined}
                      data-tour={label.toLowerCase()}
                      sx={{
                        borderRadius: 999,
                        width: 32,
                        height: 32,
                        backgroundColor: howToBgColor,
                        color: howToColor,
                        "&:hover": {
                          backgroundColor: howToHoverBg,
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                );
              }

              // DESKTOP: icon + label pill buttons
              return (
                <Button
                  key={path}
                  component={isHowTo ? "button" : (external ? "a" : Link)}
                  to={isHowTo || external ? undefined : path}
                  href={external ? path : undefined}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  startIcon={<Icon sx={{ fontSize: 18 }} />}
                  variant="contained"
                  disableElevation
                  onClick={isHowTo ? handleStartTour : undefined}
                  data-tour={label.toLowerCase()}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    px: 2,
                    minHeight: 40,
                    backgroundColor: isHowTo ? "#e91e63" : // Pink for HowTo
                      (active ? theme.palette.primary.main : bubbleBg),
                    color: isHowTo ? "#ffffff" : // White text for HowTo
                      (active ? "#fff" : theme.palette.text.primary),
                    boxShadow: active && !isHowTo
                      ? `0 0 0 1px ${alpha(theme.palette.primary.light, 0.6)} inset`
                      : "none",
                    "&:hover": {
                      backgroundColor: isHowTo ? "#d81b60" : // Darker pink on hover
                        (active ? theme.palette.primary.dark : bubbleHover),
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Paper>
        </Box>

        {/* Right: Notification Center + Theme switch + (optional email) + logout */}
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0.5 : 1.5, flexShrink: 0 }} >
          {isAuthenticated && <NotificationCenter data-tour="notification"/>}
          <Switch
            color="default"
            checked={checked}
            onChange={handleChange}
            inputProps={{ "aria-label": "toggle theme" }}
            data-tour="theme-switch"
            size={isMobile ? "small" : "medium"}
          />
          {!isMobile && email && (
            <Typography variant="body2">{email}</Typography>
          )}
          {isAuthenticated && (
            <Tooltip title={isMobile ? "Logout" : ""} arrow>
              <IconButton
                size={isMobile ? "small" : "medium"}
                onClick={logout}
                sx={{
                  borderRadius: 2,
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                  },
                }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
      
      {/* Add Tour Guide component */}
      <TourGuide 
        steps={tourSteps}
        tourId="navbar-tour"
        run={runTour}
        continuous={true}
        showSkipButton={true}
        showProgress={true}
        onFinish={handleFinishTour}
        onSkip={handleFinishTour}
      />
    </AppBar>
  );
}